// src/utils/cal.js
//
// SoKo Water Systems — calibration math helpers.
//
// This module is the single source of truth for the math relating
// (depthFt, mA) cal points to a linear regression, and for converting
// between feet and mA at runtime. Used by:
//
//   - CalibrationWizard.js : converts Code A/B input to mA, builds calPoints
//   - Settings.js          : converts feet ↔ mA on save
//   - Dashboard.js         : derives sensor-cal-only depth display
//
// A parallel implementation lives in functions/cal.js for the cloud
// function's pair-sync derivation. Keep them in sync. Both are small
// enough to verify by inspection.
//
// References:
//   - Schema doc section 4 (pair-sync derivation rules)
//   - Schema doc section 5 (validation rules)


// ─────────────────────────────────────────────────────────────────
// Regression
// ─────────────────────────────────────────────────────────────────

/**
 * Compute slope and intercept from a calPoints array.
 *
 * For 2 points: simple slope/intercept (closed form).
 * For 3+ points: ordinary least-squares linear regression.
 *
 * @param {Array<{depthFt: number, mA: number}>|null|undefined} calPoints
 * @returns {{slope: number, intercept: number} | null}
 *
 * Returns null if:
 *   - calPoints is null/undefined/missing
 *   - fewer than 2 points
 *   - all points share the same depthFt (degenerate, can't fit a line)
 */
export function regress(calPoints) {
  if (!Array.isArray(calPoints) || calPoints.length < 2) return null;

  // Filter out malformed points defensively. Cloud or web shouldn't
  // write garbage here, but if they do we'd rather fail with null
  // than NaN.
  const pts = calPoints.filter(
    p => p && typeof p.depthFt === 'number' && typeof p.mA === 'number'
  );
  if (pts.length < 2) return null;

  // Detect degenerate input (all points at the same depth).
  const firstDepth = pts[0].depthFt;
  if (pts.every(p => Math.abs(p.depthFt - firstDepth) < 1e-9)) return null;

  // 2-point closed form. Equivalent to least-squares for n=2 but
  // skips the divide-by-tiny-variance edge case.
  if (pts.length === 2) {
    const [a, b] = pts;
    const slope = (b.mA - a.mA) / (b.depthFt - a.depthFt);
    const intercept = a.mA - slope * a.depthFt;
    return { slope, intercept };
  }

  // OLS linear regression for 3+ points.
  // slope = Σ((d - dbar)(m - mbar)) / Σ((d - dbar)^2)
  // intercept = mbar - slope * dbar
  const n = pts.length;
  let sumD = 0, sumM = 0;
  for (const p of pts) { sumD += p.depthFt; sumM += p.mA; }
  const dbar = sumD / n;
  const mbar = sumM / n;

  let num = 0, den = 0;
  for (const p of pts) {
    const dd = p.depthFt - dbar;
    const dm = p.mA - mbar;
    num += dd * dm;
    den += dd * dd;
  }
  if (den === 0) return null;  // all-same-depth slipped through

  const slope = num / den;
  const intercept = mbar - slope * dbar;
  return { slope, intercept };
}


// ─────────────────────────────────────────────────────────────────
// Plausibility check on regression
// ─────────────────────────────────────────────────────────────────

// Lower bound on regression slope, in mA per foot of depth. Below this,
// captures don't form a sensible water-depth relationship — typically
// because the user clicked Capture before a fresh sensor reading arrived
// (so multiple depths paired with the same mA), or because the sensor
// wasn't actually at the typed depth.
//
// No upper bound: short-range sensors (e.g., 0-3ft spec for shallow
// tanks) legitimately produce steeper slopes. We don't want to reject
// a valid cal on those.
//
// Mirror of MIN_PLAUSIBLE_SLOPE_MA_PER_FT in functions/cal.js.
export const MIN_PLAUSIBLE_SLOPE_MA_PER_FT = 0.5;

/**
 * Check whether a calPoints array produces a regression that plausibly
 * represents water-level sensing. Returns either:
 *   { ok: true, slope, intercept }    — passes
 *   { ok: false, slope, reason }      — fails, with the failing slope for diagnostics
 *
 * Fails when:
 *   - calPoints can't fit a line at all (regress() returns null)
 *   - slope is negative (deeper → lower reading, physically impossible
 *     for a submersible pressure transducer)
 *   - slope is positive but below MIN_PLAUSIBLE_SLOPE_MA_PER_FT
 *     (suggests stale-reading capture pattern — see session 3F notes)
 *
 * Used by the wizard before save (gate the Save button) and mirrored
 * in functions/cal.js for cloud-side defense-in-depth.
 *
 * @param {Array} calPoints
 * @returns {{ok: true, slope, intercept} | {ok: false, slope?, reason: string}}
 */
export function validateRegressionPlausibility(calPoints) {
  const r = regress(calPoints);
  if (!r) {
    return { ok: false, reason: 'calPoints cannot fit a line (need 2+ points at distinct depths)' };
  }
  if (r.slope < 0) {
    return { ok: false, slope: r.slope, reason: 'negative slope (deeper readings are lower)' };
  }
  if (r.slope < MIN_PLAUSIBLE_SLOPE_MA_PER_FT) {
    return { ok: false, slope: r.slope, reason: `slope too low (${r.slope.toFixed(3)} mA/ft < ${MIN_PLAUSIBLE_SLOPE_MA_PER_FT} mA/ft minimum)` };
  }
  return { ok: true, slope: r.slope, intercept: r.intercept };
}


// ─────────────────────────────────────────────────────────────────
// Conversions
// ─────────────────────────────────────────────────────────────────

/**
 * Convert depth in feet to mA using the calibration in calPoints.
 *
 * @param {number} ft - depth in feet
 * @param {Array} calPoints - calibration points (same shape as regress)
 * @returns {number | null} - mA value, or null if calPoints can't fit a line
 */
export function feetToMa(ft, calPoints) {
  if (typeof ft !== 'number' || !isFinite(ft)) return null;
  const r = regress(calPoints);
  if (!r) return null;
  return r.slope * ft + r.intercept;
}

/**
 * Convert mA to depth in feet using the calibration in calPoints.
 *
 * @param {number} mA - sensor reading in mA
 * @param {Array} calPoints - calibration points (same shape as regress)
 * @returns {number | null} - depth in feet, or null if calPoints can't fit a line
 */
export function maToFeet(mA, calPoints) {
  if (typeof mA !== 'number' || !isFinite(mA)) return null;
  const r = regress(calPoints);
  if (!r) return null;
  if (r.slope === 0) return null;  // shouldn't happen if regress() returned non-null
  return (mA - r.intercept) / r.slope;
}


// ─────────────────────────────────────────────────────────────────
// Pair-sync helpers (for web writes)
// ─────────────────────────────────────────────────────────────────

/**
 * Given a tank's current state and a pending feet update, compute the
 * full pair (emptyFt, fullFt, emptyMa, fullMa) to write.
 *
 * Used by Settings.js and CalibrationWizard.js. Encodes Rule 1 from
 * the schema doc (web write of feet → derive mA in web client).
 *
 * @param {object} update - the user's edited values
 * @param {number|null} update.emptyFt - new emptyFt (or null to clear)
 * @param {number|null} update.fullFt - new fullFt (or null to clear)
 * @param {Array|null} calPoints - current calPoints from devices/{id}.tank{N}
 * @returns {{emptyFt, fullFt, emptyMa, fullMa}} - all four fields,
 *           with null where derivation isn't possible. Write all four
 *           atomically.
 */
export function deriveTankInfoFromFeet(update, calPoints) {
  const out = {
    emptyFt: update.emptyFt ?? null,
    fullFt:  update.fullFt  ?? null,
    emptyMa: null,
    fullMa:  null,
  };

  // If calPoints can't produce a regression, mA can't be derived.
  // Write feet, leave mA null. Tank will be in "tank info, no
  // sensor cal" state on the dashboard.
  const r = regress(calPoints);
  if (!r) return out;

  if (typeof out.emptyFt === 'number') {
    out.emptyMa = r.slope * out.emptyFt + r.intercept;
  }
  if (typeof out.fullFt === 'number') {
    out.fullMa = r.slope * out.fullFt + r.intercept;
  }
  return out;
}

/**
 * Given a new calPoints array (sensor cal change) and a tank's
 * current emptyFt/fullFt, compute the new emptyMa/fullMa to write.
 *
 * Used when user enters new factory tag values or completes a manual
 * cal. Encodes Rule 3 from the schema doc (sensor cal change →
 * recompute mA from existing feet).
 *
 * If feet aren't set yet (button-setup-only state), this returns
 * null mA values; in that case the cloud function backfills feet
 * from the device's already-pushed mA via Rule 2.
 *
 * @param {Array} newCalPoints - the new calPoints to take effect
 * @param {number|null} emptyFt - existing emptyFt
 * @param {number|null} fullFt - existing fullFt
 * @returns {{emptyMa, fullMa}} - new mA values, or null where can't derive
 */
export function deriveMaFromCalChange(newCalPoints, emptyFt, fullFt) {
  const r = regress(newCalPoints);
  if (!r) return { emptyMa: null, fullMa: null };
  return {
    emptyMa: typeof emptyFt === 'number' ? r.slope * emptyFt + r.intercept : null,
    fullMa:  typeof fullFt  === 'number' ? r.slope * fullFt  + r.intercept : null,
  };
}


// ─────────────────────────────────────────────────────────────────
// Validation — sensor cal codes (Code A / Code B from tag)
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// Factory calibration — shared physical constants
//
// Mirror of the same three definitions in watertank-cloud/functions/cal.js.
// ─────────────────────────────────────────────────────────────────

/**
 * Reference depth of the "dry" factory-cal capture, in feet.
 *
 * The firmware's guided `factory N` routine captures three points: dry
 * (sensor in air), FACTORY_CAL_LOW_FT, and FACTORY_CAL_HIGH_FT. It
 * transmits only the low/high depths — the dry point's depth is implicit.
 * This constant is that implicit value made explicit and shared, so the
 * cloud ingest and the bench script can't encode it differently.
 */
export const FACTORY_CAL_DRY_FT = 0;

/**
 * Usable span of a 4-20 mA current loop, in mA. A sensor's full-scale
 * depth is this span divided by its mA/ft slope.
 */
export const LOOP_MA_SPAN = 16;

/**
 * Full-scale depth of the attached sensor in feet, derived from a
 * regression slope — a 0-3 m sensor fits ~9.8 ft, a 0-5 m ~16.4 ft.
 *
 * For "is this the right sensor for your tank?" hints at setup time.
 * INFORMATIONAL ONLY, never a pass/fail gate: a genuinely bad sensor
 * reads outside 3.6-20.5 mA and fails the firmware's own range check
 * long before it produces a fit worth measuring, and unit-to-unit
 * variation is what calibration is for.
 *
 * Reads well against devices/{id}.factoryCal.tank{N}.fullScaleFt, which
 * carries the same number precomputed at bench time.
 *
 * @param {number} slope - mA per foot
 * @returns {number|null} feet, or null if slope is unusable
 */
export function fullScaleFtFromSlope(slope) {
  if (typeof slope !== 'number' || !isFinite(slope) || slope <= 0) return null;
  return LOOP_MA_SPAN / slope;
}


// Per schema doc section 5 / handoff 5.9. Hard-reject bounds.
export const CODE_A_MIN = 3000;
export const CODE_A_MAX = 7000;
export const CODE_B_MIN = 6500;
export const CODE_B_MAX = 14000;

/**
 * Parse a user-typed code string. Strips whitespace, commas, leading
 * zeros. Returns the integer or null if unparseable.
 *
 * @param {string} input - what the user typed
 * @returns {number | null}
 */
export function parseCode(input) {
  if (typeof input !== 'string') return null;
  const cleaned = input.replace(/[\s,]/g, '');
  if (cleaned === '') return null;
  if (!/^\d+$/.test(cleaned)) return null;  // digits only after cleaning
  const n = parseInt(cleaned, 10);
  if (!isFinite(n)) return null;
  return n;
}

/**
 * Validate parsed Code A and Code B together. Returns either:
 *   { ok: true, codeA, codeB }                    — passes
 *   { ok: false, error: 'human-readable string' } — fails
 *
 * Hard rejects on out-of-range values and on B <= A. The B-A range
 * cross-check from session 3b is intentionally NOT applied here —
 * dropped per session 3c (only two sensors measured, extrapolating
 * a tight range bound from n=2 was presumptuous).
 */
export function validateCodes(rawA, rawB) {
  const codeA = parseCode(rawA);
  const codeB = parseCode(rawB);

  if (codeA === null) {
    return { ok: false, error: 'Code A — please enter the number from the sensor tag.' };
  }
  if (codeB === null) {
    return { ok: false, error: 'Code B — please enter the number from the sensor tag.' };
  }
  if (codeA < CODE_A_MIN || codeA > CODE_A_MAX) {
    return {
      ok: false,
      error: `Code A is outside the expected range (${CODE_A_MIN}–${CODE_A_MAX}). Double-check the number on the tag.`,
    };
  }
  if (codeB < CODE_B_MIN || codeB > CODE_B_MAX) {
    return {
      ok: false,
      error: `Code B is outside the expected range (${CODE_B_MIN}–${CODE_B_MAX}). Double-check the number on the tag.`,
    };
  }
  if (codeB <= codeA) {
    return {
      ok: false,
      error: 'Code B should be larger than Code A. Make sure they\'re in the right order.',
    };
  }

  return { ok: true, codeA, codeB };
}

/**
 * Build a calPoints array from validated factory-tag codes.
 * Implied depths are 1 ft (Code A) and 4 ft (Code B) per session 3b
 * manufacturing default.
 *
 * @param {number} codeA - validated Code A integer
 * @param {number} codeB - validated Code B integer
 * @returns {Array} calPoints, ready to write
 */
export function calPointsFromCodes(codeA, codeB) {
  const ts = Date.now();
  return [
    { depthFt: 1.0, mA: codeA / 1000, source: 'factory', ts },
    { depthFt: 4.0, mA: codeB / 1000, source: 'factory', ts },
  ];
}


// ─────────────────────────────────────────────────────────────────
// Validation — tank info (empty/full feet)
// ─────────────────────────────────────────────────────────────────

/**
 * Validate empty/full feet inputs. Either may be blank. If both
 * filled, fullFt must be > emptyFt.
 *
 * Returns:
 *   { ok: true, emptyFt: number|null, fullFt: number|null, warns: [string] }
 *   { ok: false, error: string }
 *
 * Soft warns are advisory — caller can save anyway. UI should show
 * them as a "double-check?" prompt, not block save.
 *
 * Per schema doc section 5:
 *   - emptyFt > 3 → soft warn ("3 inches not 3 feet?")
 *   - fullFt > 13 → soft warn ("higher than typical sensor range")
 *   - fullFt <= emptyFt → hard reject
 */
export function validateTankInfo(rawEmpty, rawFull) {
  const emptyFt = parseFloatOrNull(rawEmpty);
  const fullFt  = parseFloatOrNull(rawFull);

  // Both blank is fine — user is skipping tank info.
  if (emptyFt === null && fullFt === null) {
    return { ok: true, emptyFt: null, fullFt: null, warns: [] };
  }

  if (emptyFt !== null && (emptyFt < 0 || !isFinite(emptyFt))) {
    return { ok: false, error: 'Empty depth must be a positive number.' };
  }
  if (fullFt !== null && (fullFt < 0 || !isFinite(fullFt))) {
    return { ok: false, error: 'Full depth must be a positive number.' };
  }
  if (emptyFt !== null && fullFt !== null && fullFt <= emptyFt) {
    return {
      ok: false,
      error: 'Full depth must be greater than empty depth.',
    };
  }

  const warns = [];
  if (emptyFt !== null && emptyFt > 3) {
    warns.push('Empty depth over 3 ft is unusual — did you mean inches?');
  }
  if (fullFt !== null && fullFt > 13) {
    warns.push('Full depth over 13 ft is higher than the typical sensor range — double-check?');
  }

  return { ok: true, emptyFt, fullFt, warns };
}

/**
 * Parse a possibly-empty form input into a number or null.
 * Accepts strings (parsed) and numbers (passed through).
 * Returns null for empty/whitespace strings or NaN.
 */
function parseFloatOrNull(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return null;
    const n = parseFloat(trimmed);
    return isFinite(n) ? n : null;
  }
  return null;
}


// ─────────────────────────────────────────────────────────────────
// Tank state machine — for the dashboard
// ─────────────────────────────────────────────────────────────────

/**
 * Determine which display state a tank is in. Drives the dashboard's
 * TankCard rendering. Per schema doc section 7.
 *
 * @param {object} tank - the tank{N} object from devices/{id}
 * @param {boolean} fault - lastReading.f{N}
 * @returns {'fault'|'nothing'|'sensor-only'|'tank-only'|'full'}
 */
export function tankDisplayState(tank, fault) {
  if (fault) return 'fault';
  if (!tank) return 'nothing';

  const hasCal  = Array.isArray(tank.calPoints) && tank.calPoints.length >= 2;
  const hasMa   = typeof tank.emptyMa === 'number' && typeof tank.fullMa === 'number';

  if (!hasCal && !hasMa) return 'nothing';
  if ( hasCal && !hasMa) return 'sensor-only';
  if (!hasCal &&  hasMa) return 'tank-only';
  return 'full';
}
